import { cellAt, headerIndex, isBlank } from './cells';
import { breakColumnLabel, sortBreakValues } from './breakColumns';
import type {
  ParsedSheet,
  PivotCollision,
  PriceBreakMapping,
  SheetPivotResult,
} from './types';

interface ColumnIndexes {
  key: number;
  break: number;
  price: number;
  carry: number[];
}

interface ItemGroup {
  key: string;
  carry: string[];
  prices: Map<string, string>;
}

function missingColumns(
  headers: readonly string[],
  mapping: PriceBreakMapping,
): string[] {
  const required = [
    mapping.keyColumn,
    mapping.breakColumn,
    mapping.priceColumn,
  ];
  return required.filter((column) => headerIndex(headers, column) === -1);
}

function columnIndexes(
  headers: readonly string[],
  mapping: PriceBreakMapping,
): ColumnIndexes {
  const used = new Set([
    mapping.keyColumn,
    mapping.breakColumn,
    mapping.priceColumn,
  ]);
  const carry: number[] = [];
  for (let index = 0; index < headers.length; index += 1) {
    const header = headers[index];
    if (header === undefined || isBlank(header) || used.has(header)) continue;
    carry.push(index);
  }
  return {
    key: headerIndex(headers, mapping.keyColumn),
    break: headerIndex(headers, mapping.breakColumn),
    price: headerIndex(headers, mapping.priceColumn),
    carry,
  };
}

function firstNonEmpty(current: string, incoming: string): string {
  return isBlank(current) ? incoming : current;
}

function recordPrice({
  prices,
  breakValue,
  price,
}: {
  prices: Map<string, string>;
  breakValue: string;
  price: string;
}): string | null {
  const previous = prices.get(breakValue);
  prices.set(breakValue, price);
  if (previous === undefined || previous === price) return null;
  return previous;
}

function fillCarry({
  carry,
  row,
  carryIndexes,
}: {
  carry: string[];
  row: readonly string[];
  carryIndexes: readonly number[];
}): void {
  for (let index = 0; index < carryIndexes.length; index += 1) {
    const column = carryIndexes[index];
    const current = carry[index];
    if (column === undefined || current === undefined) continue;
    carry[index] = firstNonEmpty(current, cellAt(row, column));
  }
}

function groupRows(
  sheet: ParsedSheet,
  columns: ColumnIndexes,
): {
  groups: ItemGroup[];
  collisions: PivotCollision[];
  breakValues: string[];
} {
  const groups = new Map<string, ItemGroup>();
  const collisions: PivotCollision[] = [];
  const breakValues = new Set<string>();

  for (const row of sheet.rows) {
    const key = cellAt(row, columns.key);
    if (isBlank(key)) continue;
    const existing = groups.get(key);
    const group =
      existing ??
      ({
        key,
        carry: columns.carry.map((column) => cellAt(row, column)),
        prices: new Map<string, string>(),
      } satisfies ItemGroup);
    if (existing) {
      fillCarry({ carry: group.carry, row, carryIndexes: columns.carry });
    } else {
      groups.set(key, group);
    }

    const breakValue = cellAt(row, columns.break);
    breakValues.add(breakValue);
    const previous = recordPrice({
      prices: group.prices,
      breakValue,
      price: cellAt(row, columns.price),
    });
    if (previous !== null) {
      collisions.push({
        sheetName: sheet.name,
        key,
        breakValue,
        previousPrice: previous,
        nextPrice: cellAt(row, columns.price),
      });
    }
  }

  return {
    groups: [...groups.values()],
    collisions,
    breakValues: sortBreakValues([...breakValues]),
  };
}

function carryHeaders(
  headers: readonly string[],
  carryIndexes: readonly number[],
): string[] {
  return carryIndexes.flatMap((index) => {
    const header = headers[index];
    return header === undefined ? [] : [header];
  });
}

function itemRow(group: ItemGroup, breakValues: readonly string[]): string[] {
  const prices = breakValues.map(
    (breakValue) => group.prices.get(breakValue) ?? '',
  );
  return [group.key, ...group.carry, ...prices];
}

export function pivotSheet(
  sheet: ParsedSheet,
  mapping: PriceBreakMapping,
): SheetPivotResult {
  const missing = missingColumns(sheet.headers, mapping);
  if (missing.length > 0) {
    return {
      status: 'missing-columns',
      sheetName: sheet.name,
      missing,
    };
  }
  const columns = columnIndexes(sheet.headers, mapping);
  const { groups, collisions, breakValues } = groupRows(sheet, columns);
  const breakColumns = breakValues.map((value) =>
    breakColumnLabel(mapping.priceColumn, value),
  );
  return {
    status: 'pivoted',
    sheet: {
      sheetName: sheet.name,
      headers: [
        mapping.keyColumn,
        ...carryHeaders(sheet.headers, columns.carry),
        ...breakColumns,
      ],
      rows: groups.map((group) => itemRow(group, breakValues)),
      sourceRowCount: sheet.rows.filter(
        (row) => !isBlank(cellAt(row, columns.key)),
      ).length,
      itemCount: groups.length,
      multiBreakItemCount: groups.filter((group) => group.prices.size > 1)
        .length,
      breakColumns,
      collisions,
    },
  };
}

export function pivotSheets(
  sheets: readonly ParsedSheet[],
  mapping: PriceBreakMapping,
): SheetPivotResult[] {
  return sheets.map((sheet) => pivotSheet(sheet, mapping));
}
